import { Dispatch, ReactNode, useMemo } from 'react';
import { ITab, pageTypeEnum } from '@/components/email-finder/page-client';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import {
  IconAIDiscover,
  IconFollower,
  IconFollowing,
  IconHashtag,
  IconKeyword,
  IconLocation,
  IconNewMb,
  IconNewPC,
} from '@/components/email-finder/icons';
import { Button, Tooltip } from 'antd';
import { useTranslation } from 'next-i18next';
import { EventName } from '@/shared/event-types';
import { useRouter } from 'next/router';
import useCheckIsMobile from '@/lib/hooks/useCheckIsMobile';

export const tabs: ITab[] = [
  {
    tab: 'followers',
    title: 'By Follower',
    label: 'follower',
    Icon: IconFollower,
    modalTitle: 'Extract from Followers',
    modalDesc: 'Collect publicly available emails from Instagram users who follow this account.',
    placeholder: 'Enter a username to get follower emails.',
    tagName: EventName.extract_followers_start_click,
    tagBuyName: EventName.extract_followers_add_credit_click,
    tooltip: 'Get email addresses of people who follow this account.',
  },
  {
    tab: 'followings',
    title: 'By Following',
    label: 'following',
    Icon: IconFollowing,
    modalTitle: 'Extract from Following',
    modalDesc: 'Collect publicly available emails from the accounts this user follows.',
    placeholder: 'Enter a username to get following emails.',
    tagName: EventName.extract_followings_start_click,
    tagBuyName: EventName.extract_followings_add_credit_click,
    tooltip: 'Get email addresses of accounts this profile is following.',
  },
  {
    tab: 'location',
    title: 'By Location',
    label: 'location',
    Icon: IconLocation,
    modalTitle: 'Extract by Location URL',
    modalDesc: 'Collect publicly available emails from Instagram users who posted at a specific location.',
    placeholder: 'Enter a city or place name (e.g. New York).',
    tagName: EventName.extract_location_start_click,
    tagBuyName: EventName.extract_location_add_credit_click,
    tooltip: 'Get email addresses of people who post from this location.',
  },
  {
    tab: 'keyword',
    title: 'By Keyword',
    label: 'keyword',
    Icon: IconKeyword,
    modalTitle: 'Keyword Email Search',
    modalDesc: 'Extract verified emails from Instagram profiles using keyword search.',
    placeholder: 'Enter a keyword (e.g. “makeup artist”).',
    tagName: EventName.extract_keyword_start_click,
    tagBuyName: EventName.extract_keyword_add_credit_click,
    tooltip: 'Find emails from users matching this keyword.',
  },
  {
    tab: 'hashtag',
    title: 'By Hashtag',
    label: 'hashtag',
    Icon: IconHashtag,
    modalTitle: 'Extract by Hashtag',
    modalDesc: 'Collect publicly available emails from Instagram users who posted with this hashtag.',
    placeholder: 'Enter a hashtag (e.g. fashion).',
    tagName: EventName.extract_hashtag_start_click,
    tagBuyName: EventName.extract_hashtag_add_credit_click,
    tooltip: 'Find emails from users engaging with this hashtag.',
  },
  {
    tab: 'ai-discover',
    title: 'AI Discover',
    label: 'ai-discover',
    Icon: IconAIDiscover,
    // todo 待完善
    modalTitle: '',
    modalDesc: '',
    placeholder: 'Enter a hashtag or hashtag URL.',
    tagName: EventName.extract_hashtag_start_click,
    tagBuyName: EventName.extract_hashtag_add_credit_click,
    tooltip: 'Let AI find the best email address automatically.',
  },
];

interface IHero {
  title: string;
  subTitle: string;
  desc: string;
  tags: string[];
}

export default function Hero({
  children,
  data,
  currentTab,
  pageType,
  setCurrentTabAction,
  userCreditInfo,
  isLogin,
  // guideToRegisterOrLogin,
}: {
  children: ReactNode;
  data: IHero;
  currentTab: ITab['tab'];
  pageType: pageTypeEnum;
  setCurrentTabAction: Dispatch<ITab['tab']>;
  userCreditInfo: any;
  isLogin: boolean;
  guideToRegisterOrLogin: () => void;
}) {
  const { t } = useTranslation('common');
  const LinkTag = pageType === pageTypeEnum.subPage ? Link : 'button';
  const router = useRouter();
  const { isMobile } = useCheckIsMobile(1024);

  const showTabs = useMemo(() => {
    // 别修改原数组
    if (isMobile) {
      const tabsCopy = [...tabs]; // 创建数组副本
      const aIDiscoverIndex = tabsCopy.findIndex((tab) => tab.tab === 'ai-discover');
      if (aIDiscoverIndex !== -1) {
        const aIDiscover = tabsCopy.splice(aIDiscoverIndex, 1);
        tabsCopy.splice(2, 0, aIDiscover[0]);
      }
      return tabsCopy;
    }
    return tabs;
  }, [isMobile]);

  return (
    <section className="pt-20 text-primary max-w-[1100px] mx-auto px-4 lg:pt-26">
      <h1 className="font-bold lg:text-[42px] text-[32px] flex flex-col gap-2 text-center">
        <span className="text-[#725CFF]">{data.title}</span>
      </h1>
      <p className="mt-4 lg:mt-0 font-bold lg:text-[42px] text-[32px] lg:leading-[48px] leading-[32px] flex flex-col gap-2 text-center">
        {data.subTitle}
      </p>

      <div className="pt-4 lg:pt-9 text-center font-medium max-w-3xl mx-auto">{data.desc}</div>
      <div className="grid grid-cols-4 max-md:grid-cols-2 gap-4 items-center justify-between pt-4 lg:pt-6">
        <style jsx>{`
          .tag-text > :global(span) {
            font-weight: 700;
            color: #725cff;
          }
        `}</style>
        {data.tags.map((tag, i) => (
          <div key={i} className="flex items-center justify-start lg:justify-center">
            <Check className="text-[#38C13D] lg:mr-1" />
            <span className="tag-text text-sm lg:text-base" dangerouslySetInnerHTML={{ __html: tag }} />
          </div>
        ))}
      </div>
      <div className="mt-[54px] flex bg-[#ECEDF4] lg:w-fit mx-auto lg:border border-2 border-x-0 border-[#D9D9D9] lg:rounded-md fixed w-[100vw] max-lg:overflow-x-auto scrollbar-hide top-2 left-0 right-0 z-50 lg:relative">
        {showTabs.map((item, index) => (
          <Tooltip title={!isMobile ? item.tooltip : null} key={item.tab} trigger="hover">
            <LinkTag
              className={cn(
                'py-2 flex items-center text-nowrap justify-center text-sm h-10 group hover:text-[#725CFF] relative',
                { 'text-[#725CFF] bg-white': currentTab === item.tab },
                { 'rounded-l-md': index === 0 },
                { 'rounded-r-md': index === tabs.length - 1 }
              )}
              href={
                pageType === pageTypeEnum.subPage && item.tab === 'ai-discover'
                  ? `/instagram-email-finder`
                  : `/instagram-email-finder/${item.tab}`
              }
              onClick={(e) => {
                if (pageType === pageTypeEnum.subPage && item.tab === 'ai-discover') {
                  e.preventDefault();
                  sessionStorage.setItem('ins_email_finder_tab', 'ai-discover');
                  router.push(`/instagram-email-finder`);
                  return;
                }
                setCurrentTabAction(item.tab);
              }}
            >
              {item.tab === 'ai-discover' &&
                (isMobile ? (
                  <IconNewMb className="absolute top-0 right-0" />
                ) : (
                  <IconNewPC className="absolute -top-5 -right-2" />
                ))}
              <span
                className={cn('flex items-center border-r px-5', {
                  'border-r-transparent': currentTab === item.tab,
                })}
              >
                <item.Icon className="w-4 h-4 mr-1.5" />
                {t(item.title)}
              </span>
            </LinkTag>
          </Tooltip>
        ))}
      </div>
      {currentTab !== 'ai-discover' && (
        <>
          {children}
          <div className="flex items-center justify-around max-w-[560px] mx-auto pt-6 text-sm font-semibold">
            <div>
              {t('Credits')}:{' '}
              <span className="text-[#38C13D] font-semibold">{isLogin ? userCreditInfo?.remaining_credits : 100}</span>
            </div>
            <div>
              {t('Current Plan')}:{' '}
              <span className="text-[#38C13D] font-semibold">{isLogin ? userCreditInfo?.package_type : 'Free'}</span>
              {userCreditInfo?.package_type != 'Business' && (
                <Button
                  className="font-semibold text-sm"
                  type="link"
                  onClick={() => {
                    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {t('Upgrade')}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
